import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area'
import * as React from 'react'
import { twMerge } from 'tailwind-merge'

type DragDirection = 'horizontal' | 'vertical' | 'both'

interface DragState {
  isDragging: boolean
  startPosition: { x: number; y: number }
  initialScroll: { left: number; top: number }
  viewport: HTMLElement | null
}

const useDragScroll = (enableDrag: boolean, direction: DragDirection) => {
  const dragState = React.useRef<DragState>({
    isDragging: false,
    startPosition: { x: 0, y: 0 },
    initialScroll: { left: 0, top: 0 },
    viewport: null,
  })

  const findViewport = (element: HTMLElement): HTMLElement | null => {
    return element.querySelector('[data-radix-scroll-area-viewport]')
  }

  const shouldScrollHorizontally = React.useCallback((): boolean => {
    return direction === 'horizontal' || direction === 'both'
  }, [direction])

  const shouldScrollVertically = React.useCallback((): boolean => {
    return direction === 'vertical' || direction === 'both'
  }, [direction])

  const updateScroll = React.useCallback(
    (deltaX: number, deltaY: number): void => {
      const { viewport, initialScroll } = dragState.current
      if (!viewport) return

      if (shouldScrollHorizontally()) {
        viewport.scrollLeft = initialScroll.left - deltaX
      }

      if (shouldScrollVertically()) {
        viewport.scrollTop = initialScroll.top - deltaY
      }
    },
    [shouldScrollHorizontally, shouldScrollVertically],
  )

  const initializeDrag = React.useCallback(
    (event: React.MouseEvent, viewport: HTMLElement): void => {
      dragState.current = {
        isDragging: true,
        startPosition: { x: event.clientX, y: event.clientY },
        initialScroll: { left: viewport.scrollLeft, top: viewport.scrollTop },
        viewport,
      }

      event.preventDefault()
    },
    [],
  )

  const handleMouseMove = React.useCallback(
    (event: MouseEvent): void => {
      const { isDragging, startPosition, viewport } = dragState.current

      if (!isDragging || !viewport) return

      event.preventDefault()

      const deltaX = event.clientX - startPosition.x
      const deltaY = event.clientY - startPosition.y

      updateScroll(deltaX, deltaY)
    },
    [updateScroll],
  )

  const handleMouseUp = React.useCallback((): void => {
    if (!dragState.current.isDragging) return

    dragState.current.isDragging = false
    dragState.current.viewport = null

    // Cleanup event listeners
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
    document.removeEventListener('mouseleave', handleMouseUp)
  }, [handleMouseMove])

  const handleMouseDown = React.useCallback(
    (event: React.MouseEvent): void => {
      if (!enableDrag) return

      const target = event.currentTarget as HTMLElement
      const viewport = findViewport(target)

      if (!viewport) return

      initializeDrag(event, viewport)

      // Add event listeners
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.addEventListener('mouseleave', handleMouseUp)
    },
    [handleMouseMove, handleMouseUp, enableDrag, initializeDrag],
  )

  return {
    onMouseDown: handleMouseDown,
  }
}

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & {
    enableDrag?: boolean
    dragDirection?: DragDirection
  }
>(
  (
    {
      className,
      children,
      enableDrag = false,
      dragDirection = 'horizontal',
      ...props
    },
    ref,
  ) => {
    const dragProps = useDragScroll(enableDrag, dragDirection)

    return (
      <ScrollAreaPrimitive.Root
        ref={ref}
        className={twMerge('relative overflow-hidden', className)}
        {...props}
        {...(enableDrag && dragProps)}
      >
        <ScrollAreaPrimitive.Viewport
          className={twMerge(
            'h-full w-full rounded-[inherit]',
            enableDrag && 'cursor-grab active:cursor-grabbing',
          )}
        >
          {children}
        </ScrollAreaPrimitive.Viewport>
        <ScrollBar />
        <ScrollAreaPrimitive.Corner />
      </ScrollAreaPrimitive.Root>
    )
  },
)
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = 'vertical', ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={twMerge(
      'flex touch-none select-none transition-colors',
      orientation === 'vertical' &&
        'h-full w-2.5 border-l border-l-transparent p-px',
      orientation === 'horizontal' &&
        'h-2.5 flex-col border-t border-t-transparent p-px',
      className,
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-zinc-400" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
))
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName

export { ScrollArea, ScrollBar }
export type { DragDirection, DragState }
